import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { POSTHOG } from "@/constants";
import { useDocumentVisible } from "@/hooks/useDocumentVisible";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { getSummary, subscribe } from "@/services/chatRunTracker";
import {
  applyTabStatus,
  applyWorkingFrame,
  captureTabStatusDom,
  isWorkingDimFrame,
  resolveTabStatus,
  restoreTabStatus,
  WORKING_BLINK_INTERVAL_MS,
} from "@/utils/tabStatus";

export function useTabStatus(): boolean {
  const summary = useSyncExternalStore(subscribe, getSummary, () => "idle|0");
  const tabVisible = useDocumentVisible();
  const prefersReducedMotion = usePrefersReducedMotion();
  const { enabled, loaded } = useFeatureFlag(POSTHOG.FEATURE_FLAGS.TAB_STATUS);
  const { trackEvent } = usePostHogTracking();
  const [domSnapshot] = useState(captureTabStatusDom);
  const lastWrittenTitleRef = useRef<string | null>(null);
  const baseTitleRef = useRef(domSnapshot.title);
  const previousVisibleRef = useRef(tabVisible);
  const hiddenAtRef = useRef<number | null>(tabVisible ? null : Date.now());
  const hiddenIndicatorRef = useRef<"done" | "error" | null>(null);
  const [baseTitle, setBaseTitle] = useState(domSnapshot.title);

  const resolved = resolveTabStatus(summary, tabVisible);
  const { count, status, titlePrefix } = resolved;

  useEffect(() => {
    baseTitleRef.current = baseTitle;
  }, [baseTitle]);

  useEffect(() => {
    const titleElement = document.querySelector("title");
    if (!titleElement) return;

    const observer = new MutationObserver(() => {
      const nextTitle = document.title;
      if (nextTitle === lastWrittenTitleRef.current) return;
      setBaseTitle(nextTitle);
    });
    observer.observe(titleElement, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!loaded || !enabled || status === "idle") {
      lastWrittenTitleRef.current = baseTitle;
      restoreTabStatus(domSnapshot, baseTitle);
      return;
    }

    lastWrittenTitleRef.current = `${titlePrefix}${baseTitle}`;
    applyTabStatus(domSnapshot, { count, status, titlePrefix }, baseTitle);
  }, [baseTitle, count, domSnapshot, enabled, loaded, status, titlePrefix]);

  // Blink the favicon while a run is working, in both visible and hidden tabs, so
  // the tab shows the run is still going. Each tick reads the
  // current frame from the DOM, so a re-apply of the static icon by the effect
  // above cannot knock the blink out of step. Cleanup clears the timer and puts
  // back the static working icon before the effect above writes the next state.
  // Under `prefers-reduced-motion` the static working icon stays, including when
  // the setting is turned on mid-blink.
  useEffect(() => {
    if (!loaded || !enabled || status !== "working" || prefersReducedMotion) {
      return;
    }

    const interval = setInterval(() => {
      applyWorkingFrame(domSnapshot, !isWorkingDimFrame(domSnapshot));
    }, WORKING_BLINK_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      applyWorkingFrame(domSnapshot, false);
    };
  }, [domSnapshot, enabled, loaded, prefersReducedMotion, status]);

  useEffect(() => () => {
    lastWrittenTitleRef.current = baseTitleRef.current;
    restoreTabStatus(domSnapshot, baseTitleRef.current);
  }, [domSnapshot]);

  useEffect(() => {
    const wasVisible = previousVisibleRef.current;
    previousVisibleRef.current = tabVisible;

    if (!tabVisible) {
      if (wasVisible) hiddenAtRef.current = Date.now();
      if (enabled && loaded && (status === "done" || status === "error")) {
        hiddenIndicatorRef.current = status;
      } else {
        hiddenIndicatorRef.current = null;
      }
      return;
    }

    if (
      !wasVisible &&
      enabled &&
      loaded &&
      hiddenIndicatorRef.current === status &&
      (status === "done" || status === "error") &&
      hiddenAtRef.current != null
    ) {
      trackEvent(POSTHOG.EVENTS.TAB_STATUS_RETURN, {
        status: hiddenIndicatorRef.current,
        seconds_hidden: Math.max(0, Math.round((Date.now() - hiddenAtRef.current) / 1000)),
      });
    }
    hiddenAtRef.current = null;
    hiddenIndicatorRef.current = null;
  }, [enabled, loaded, status, tabVisible, trackEvent]);

  return tabVisible;
}
